/**
 * MobileOnboardingTour — iter 190 Sprint 5 deliverable.
 *
 * iOS-style bottom-sheet onboarding for mobile devices (md:hidden).
 * Replaces the desktop react-joyride tour with a swipeable carousel:
 *   • 5 large cards with icons + title + body
 *   • Frosted glass backdrop, slide-up sheet animation
 *   • Dots pagination + Skip / Next / Tovább / Mehet buttons
 *   • Localized: HU / SK
 *   • Marks ALL the dismissal keys (sync with desktop tour)
 *
 * Only runs on mobile (uses useIsMobile). Desktop users still see react-joyride.
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Sparkles, Users, FileText, Workflow, FileBarChart, X, ChevronRight, Check,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import useIsMobile from '../utils/useIsMobile';

const STORAGE_KEY_PREFIX = 'onboarding_completed_';

const buildMobileSteps = (lang) => {
  const hu = lang === 'hu';
  return [
    {
      icon: Sparkles,
      color: '#B85A26',
      title: hu ? 'Üdv az acountix-ban!' : 'Vitajte v acountix!',
      body: hu
        ? 'Az okos magyar könyvelés mobilon és asztali gépen. Lapozz tovább!'
        : 'Inteligentné účtovníctvo na mobile aj na desktope. Pokračuj!',
    },
    {
      icon: Users,
      color: '#33416A',
      title: hu ? 'Ügyfelek egy helyen' : 'Klienti na jednom mieste',
      body: hu
        ? 'Add hozzá az ügyfeleidet, csoportosítsd őket. Drag-and-drop átsorolás.'
        : 'Pridaj klientov, zoskupuj ich. Drag-and-drop presúvanie.',
    },
    {
      icon: FileText,
      color: '#10B981',
      title: hu ? 'Számlák villámgyorsan' : 'Faktúry rýchlo',
      body: hu
        ? '8 dokumentumtípus, Billingo + Faktury integrációval. Egy gomb = NAV.'
        : '8 typov dokumentov, integrácia Billingo + Faktury. Jedno tlačidlo = NAV.',
    },
    {
      icon: FileBarChart,
      color: '#F59E0B',
      title: hu ? 'Árajánlatok aláírással' : 'Ponuky s podpisom',
      body: hu
        ? '10 sablon, AI varázsló, élő PDF-előnézet. Digitális aláírás 1 kattintással.'
        : '10 šablón, AI sprievodca, živý PDF náhľad. Digitálny podpis 1 klikom.',
    },
    {
      icon: Workflow,
      color: '#EC4899',
      title: hu ? 'AI automatizáció' : 'AI automatizácia',
      body: hu
        ? 'Visszatérő szolgáltatások AI-felismerése, cash-flow előrejelzés, ügyfél-szegmentálás.'
        : 'AI rozpoznávanie opakujúcich služieb, prognóza cash-flow, segmentácia klientov.',
    },
  ];
};

export default function MobileOnboardingTour() {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const { isMobile } = useIsMobile(768);
  const [open, setOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  const isHu = i18n.language === 'hu';
  const steps = buildMobileSteps(i18n.language);
  const storageKey = user?.id ? `${STORAGE_KEY_PREFIX}${user.id}` : null;

  useEffect(() => {
    if (!user || !isMobile || !storageKey) return;
    // Debug shortcut for QA / Playwright — force the tour by setting localStorage.
    if (localStorage.getItem('debug_mobile_onboarding') === '1') {
      setOpen(true);
      return;
    }
    if (localStorage.getItem(storageKey)) return;
    if (
      localStorage.getItem('onboardingCompleted') === 'true' ||
      localStorage.getItem('hasSeenOnboarding') === 'true' ||
      localStorage.getItem('onboarding_dismissed') === '1'
    ) {
      localStorage.setItem(storageKey, '1');
      return;
    }
    if (user.onboarding_seen) {
      localStorage.setItem(storageKey, '1');
      return;
    }
    const tmr = setTimeout(() => setOpen(true), 1000);
    return () => clearTimeout(tmr);
  }, [user, isMobile, storageKey]);

  const markDone = () => {
    if (storageKey) localStorage.setItem(storageKey, '1');
    localStorage.setItem('onboardingCompleted', 'true');
    localStorage.setItem('hasSeenOnboarding', 'true');
    setOpen(false);
  };

  const next = () => {
    if (stepIndex >= steps.length - 1) {
      markDone();
    } else {
      setStepIndex(stepIndex + 1);
    }
  };

  if (!isMobile || !open) return null;

  const step = steps[stepIndex];
  const Icon = step.icon;
  const isLast = stepIndex >= steps.length - 1;

  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col"
      data-testid="mobile-onboarding-tour"
      style={{
        background: 'rgba(15,23,42,0.55)',
        backdropFilter: 'blur(8px) saturate(170%)',
        WebkitBackdropFilter: 'blur(8px) saturate(170%)',
      }}
    >
      {/* Top — Skip button */}
      <div className="flex justify-end p-4">
        <button
          onClick={markDone}
          data-testid="mobile-onboarding-skip"
          className="px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5"
          style={{
            background: 'rgba(255,255,255,0.16)',
            color: '#FFFFFF',
            backdropFilter: 'blur(8px)',
          }}
          aria-label={isHu ? 'Kihagyás' : 'Preskočiť'}
        >
          <X size={14} /> {isHu ? 'Kihagyás' : 'Preskočiť'}
        </button>
      </div>

      {/* Center content card — slide-up sheet */}
      <div className="flex-1 flex items-end pb-6 px-4">
        <div
          className="w-full rounded-[28px] p-6 relative overflow-hidden"
          style={{
            background: 'rgba(255,255,255,0.96)',
            backdropFilter: 'blur(20px) saturate(180%)',
            boxShadow: '0 20px 60px -10px rgba(0,0,0,0.4)',
            animation: 'mobile-tour-slide-up 0.35s cubic-bezier(0.16,1,0.3,1)',
          }}
          data-testid={`mobile-tour-step-${stepIndex}`}
        >
          {/* Icon */}
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5"
            style={{
              background: `linear-gradient(135deg, ${step.color} 0%, ${step.color}cc 100%)`,
              boxShadow: `0 8px 22px -5px ${step.color}66`,
            }}
          >
            <Icon size={28} color="#FFFFFF" strokeWidth={2.2} />
          </div>

          {/* Title + body */}
          <h2
            className="text-2xl font-bold mb-2"
            style={{ color: '#0F172A', letterSpacing: '-0.02em', lineHeight: 1.15 }}
          >
            {step.title}
          </h2>
          <p
            className="text-sm leading-relaxed mb-6"
            style={{ color: '#475569' }}
          >
            {step.body}
          </p>

          {/* Dots pagination */}
          <div className="flex items-center justify-center gap-1.5 mb-5">
            {steps.map((_, i) => (
              <div
                key={i}
                className="transition-all duration-300"
                style={{
                  width: i === stepIndex ? 22 : 6,
                  height: 6,
                  borderRadius: 3,
                  background: i === stepIndex ? step.color : 'rgba(148,163,184,0.5)',
                }}
              />
            ))}
          </div>

          {/* Next/Done button */}
          <button
            onClick={next}
            data-testid={`mobile-tour-${isLast ? 'done' : 'next'}`}
            className="w-full py-3.5 rounded-2xl text-sm font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform"
            style={{
              background: `linear-gradient(135deg, ${step.color} 0%, ${step.color}cc 100%)`,
              color: '#FFFFFF',
              boxShadow: `0 6px 18px -4px ${step.color}aa`,
            }}
          >
            {isLast
              ? <>{isHu ? 'Mehet!' : 'Začnime!'} <Check size={16} /></>
              : <>{isHu ? 'Tovább' : 'Pokračovať'} <ChevronRight size={16} /></>}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes mobile-tour-slide-up {
          from { transform: translateY(40px); opacity: 0; }
          to   { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
